'use client';

import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import AmbassadorPart from './AmbassadorPart';
import Banner from './Banner';
import BecomeAmbassador from './BecomeAmbassador';
import BenefitArr from './BenefitArr';
import DownloadGuide from './DownloadGuide';
import FaqPart from './FaqPart';
import IntroPart from './IntroPart';

// 类型定义
interface AmbassadorData {
  banner?: any;
  intro_part?: any;
  become_ambassador?: any;
  benefit_arr?: any;
  ambassador_part?: any;
  faq_part?: any;
  download_guide?: any;
  seo_data?: any;
}

interface Props {
  ambassadorData: AmbassadorData;
  deviceType: EPlatform;
}

export default function AmbassadorContent({ ambassadorData, deviceType }: Props) {
  // App相关状态
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // ✅ 使用新的埋点系统
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 清空当前用户信息
      setToken('');
      setUserInfo({});

      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (token) => {
          logger.log('App登录token:', token);
          if (token !== 'ClearToken') {
            setToken(token);
            // 获取用户信息
            await refreshAccountInfo();
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 替代Vue的mounted生命周期
  useEffect(() => {
    // 神策页面埋点
    SDPageView();
  }, []);

  // ✅ 神策数据埋点函数 - 使用新的埋点系统
  const SDPageView = () => {
    try {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'AmbassadorHomePage_View',
          properties: {
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('AmbassadorHomePage_View tracking error:', e);
    }
  };

  // ✅ 神策曝光埋点函数 - 使用新的埋点系统
  const SDPageExp = (
    mkt_name: string,
    pits_index: number,
    pits_jump_type?: string,
    pits_jump_content?: string,
  ) => {
    try {
      const sdEventParams: any = {
        mkt_name,
        pits_index,
      };

      if (pits_jump_type) {
        sdEventParams.pits_jump_type = pits_jump_type;
      }
      if (pits_jump_content) {
        sdEventParams.pits_jump_content = pits_jump_content;
      }

      trackEvent(
        {
          eventName: 'AmbassadorHomePageMkt_Exp',
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('AmbassadorHomePageMkt_Exp tracking error:', e);
    }
  };

  return (
    <Box className="w-full">
      {/* 统一布局容器 - 使用项目定义的颜色和响应式设计 */}
      <Box className="w-full">
        {/* Banner组件 */}
        {ambassadorData.banner && <Banner pageData={ambassadorData.banner} sDPageExp={SDPageExp} />}

        {/* IntroPart组件 */}
        {ambassadorData.intro_part && (
          <IntroPart pageData={ambassadorData.intro_part} sDPageExp={SDPageExp} />
        )}

        {/* BecomeAmbassador组件 */}
        {ambassadorData.become_ambassador && (
          <BecomeAmbassador
            pageData={ambassadorData.become_ambassador}
            sDPageExp={SDPageExp}
            deviceType={deviceType}
          />
        )}

        {/* BenefitArr组件 */}
        {ambassadorData.benefit_arr && (
          <BenefitArr
            pageData={ambassadorData.benefit_arr}
            sDPageExp={SDPageExp}
            deviceType={deviceType}
          />
        )}

        {/* AmbassadorPart组件 */}
        {ambassadorData.ambassador_part && (
          <AmbassadorPart
            pageData={ambassadorData.ambassador_part}
            sDPageExp={SDPageExp}
            deviceType={deviceType}
          />
        )}

        {/* FaqPart组件 */}
        {ambassadorData.faq_part && (
          <FaqPart pageData={ambassadorData.faq_part} deviceType={deviceType} />
        )}

        {/* DownloadGuide组件 */}
        {ambassadorData.download_guide && (
          <DownloadGuide pageData={ambassadorData.download_guide} sDPageExp={SDPageExp} />
        )}
      </Box>
    </Box>
  );
}
